import React, { useEffect, useState } from "react";
import {
  getDist,
  getTaluka,
  getVillage,
  getCity,
  createUser,
  updateUser,
} from "../../services/auth";
import Dropdown from "../Dropdown";

const toLocalDate = (dateStr) => {
  const date = new Date(dateStr);
  const offset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() - offset);
  return date.toISOString().split("T")[0];
};

const UserDetailForm = ({ user, onClose }) => {
  // State for dropdown options
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [cities, setCities] = useState([]);

  // Loading states
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingTalukas, setIsLoadingTalukas] = useState(false);
  const [isLoadingVillages, setIsLoadingVillages] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  // State for form validation errors
  const [formErrors, setFormErrors] = useState({});

  // State for form data
  const [formData, setFormData] = useState({
    pName: "",
    bDate: "",
    pAdd: "",
    Edu: "",
    dId: "",
    Dist: "",
    tId: "",
    Taluka: "",
    vId: "",
    Village: "",
    bGroup: "",
    Mob: "",
    whtNo: "",
    Prof: "",
    Designation: "",
    eMail: "",
    Resident: "",
    cId: "",
    City: "",
  });

  // Helper function to load talukas
  const loadTalukasForDistrict = async (districtId) => {
    try {
      setIsLoadingTalukas(true);
      setTalukas([]);
      setFormData((prev) => ({ ...prev, tId: "", vId: "" }));

      if (!districtId) return;

      const data = await getTaluka(districtId);
      setTalukas(data || []);

      // If editing and this is the user's district, find and set the taluka
      if (user && user.dId === districtId && user.tId) {
        const taluka = data.find((t) => t.ID === Number(user.tId));
        if (taluka) {
          setFormData((prev) => ({
            ...prev,
            tId: user.tId,
            Taluka: taluka.Taluka || "",
          }));
          // Also load villages for this taluka
          await loadVillagesForTaluka(user.tId);
        }
      }
    } catch (err) {
      console.error("Error fetching talukas:", err);
      setTalukas([]);
    } finally {
      setIsLoadingTalukas(false);
    }
  };

  // Helper function to load villages
  const loadVillagesForTaluka = async (talukaId) => {
    try {
      setIsLoadingVillages(true);
      setVillages([]);
      setFormData((prev) => ({ ...prev, vId: "" }));

      if (!talukaId) return;

      const data = await getVillage(talukaId);
      setVillages(data || []);

      // If editing and this is the user's taluka, find and set the village
      if (user && user.tId === talukaId && user.vId) {
        const village = data.find((v) => v.ID === Number(user.vId));
        if (village) {
          setFormData((prev) => ({
            ...prev,
            vId: user.vId,
            Village: village.Gaam || "",
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching villages:", err);
      setVillages([]);
    } finally {
      setIsLoadingVillages(false);
    }
  };

  // Effect to fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingDistricts(true);
        setIsLoadingCities(true);

        const [districtData, cityData] = await Promise.all([
          getDist(),
          getCity(),
        ]);

        setDistricts(districtData || []);
        setCities(cityData || []);

        // Set initial form data if user prop exists
        if (user) {
          setFormData({
            ID: user.ID || "",
            pName: user.pName || "",
            bDate: user.bDate ? toLocalDate(user.bDate) : "",
            pAdd: user.pAdd || "",
            Edu: user.Edu || "",
            dId: user.dId || "",
            Dist: user.Dist || "",
            tId: user.tId || "",
            Taluka: user.Taluka || "",
            vId: user.vId || "",
            Village: user.Village || "",
            bGroup: user.bGroup || "",
            Mob: user.Mob || "",
            whtNo: user.whtNo || "",
            Prof: user.Prof || "",
            Designation: user.Designation || "",
            eMail: user.eMail || "",
            Resident: user.Resident || "",
            cId: user.cId || "",
            City: user.City || "",
          });

          // Load dependent data if needed
          if (user.dId) {
            await loadTalukasForDistrict(user.dId);
          }
          if (user.cId) {
            const city = cityData.find((c) => c.ID === Number(user.cId));
            if (city) {
              setFormData((prev) => ({ ...prev, City: city.City || "" }));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching initial dropdown data:", err);
      } finally {
        setIsLoadingDistricts(false);
        setIsLoadingCities(false);
      }
    };

    fetchInitialData();
  }, [user]);

  // Effect to fetch talukas when district changes
  useEffect(() => {
    if (formData.dId) {
      loadTalukasForDistrict(formData.dId);
    }
  }, [formData.dId]);

  // Effect to fetch villages when taluka changes
  useEffect(() => {
    if (formData.tId) {
      loadVillagesForTaluka(formData.tId);
    }
  }, [formData.tId]);

  // Handler for standard input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Handler for Dropdown changes
  const handleDropdownChange = (name, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      // Update related fields when dropdowns change
      if (name === "dId") {
        const dist = districts.find((d) => d.ID === Number(value));
        return { ...newData, Dist: dist?.Dist || "", tId: "", vId: "" };
      } else if (name === "tId") {
        const taluka = talukas.find((t) => t.ID === Number(value));
        return { ...newData, Taluka: taluka?.Taluka || "", vId: "" };
      } else if (name === "vId") {
        const village = villages.find((v) => v.ID === Number(value));
        return { ...newData, Village: village?.Gaam || "" };
      } else if (name === "cId") {
        const city = cities.find((c) => c.ID === Number(value));
        return { ...newData, City: city?.City || "" };
      }
      return newData;
    });

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Form validation logic
  const validateForm = () => {
    let errors = {};
    if (!formData.pName?.trim()) errors.pName = "Person Name is required";
    if (!formData.bDate) errors.bDate = "Birth Date is required";
    if (!formData.pAdd?.trim()) errors.pAdd = "Address is required";
    if (!formData.dId) errors.dId = "District is required";
    if (!formData.tId) errors.tId = "Taluka is required";
    if (!formData.vId) errors.vId = "Village is required";
    if (!formData.Designation?.trim())
      errors.Designation = "Designation is required";

    if (!formData.cId) errors.cId = "City is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handler for the save button click
  const handleSave = async () => {
    if (validateForm()) {
      try {
        const dataToSave = { ...formData };
        let result;
        if (dataToSave.ID) {
          result = await updateUser(dataToSave);
        } else {
          result = await createUser(dataToSave);
        }
        alert("User details saved successfully!");
        onClose();
      } catch (error) {
        console.error("Error saving user details:", error);
        setFormErrors({
          _general: "Failed to save user details. Please try again.",
        });
      }
    } else {
      console.log("Form validation failed:", formErrors);
    }
  };

  // Helper function to get input class names with error highlighting
  const getInputClassName = (fieldName) => {
    return `shadow appearance-none border ${
      formErrors[fieldName] ? "border-red-500" : "border-gray-300"
    } rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`;
  };

  return (
    <div className="max-w-6xl w-full mx-auto p-6 md:p-4 bg-white shadow-xl rounded-xl">
      <h5 className="text-3xl md:text-2xl font-semibold text-gray-800 text-center mb-8">
        User Detail Form
      </h5>

      {formErrors._general && (
        <p className="text-red-500 text-center mb-4">{formErrors._general}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {/* Person Name */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="pName"
          >
            Person Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="pName"
            name="pName"
            placeholder="Enter Person Name"
            value={formData.pName}
            onChange={handleInputChange}
            className={getInputClassName("pName")}
            aria-invalid={!!formErrors.pName}
            aria-describedby={formErrors.pName ? "pName-error" : undefined}
          />
          {formErrors.pName && (
            <p id="pName-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.pName}
            </p>
          )}
        </div>

        {/* Birth Date */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="bDate"
          >
            Birth Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="bDate"
            name="bDate"
            value={formData.bDate}
            onChange={handleInputChange}
            className={getInputClassName("bDate")}
            aria-invalid={!!formErrors.bDate}
            aria-describedby={formErrors.bDate ? "bDate-error" : undefined}
            max={new Date().toISOString().split("T")[0]}
          />
          {formErrors.bDate && (
            <p id="bDate-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.bDate}
            </p>
          )}
        </div>

        {/* Address */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="pAdd"
          >
            Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="pAdd"
            name="pAdd"
            placeholder="Enter Address"
            value={formData.pAdd}
            onChange={handleInputChange}
            className={getInputClassName("pAdd")}
            aria-invalid={!!formErrors.pAdd}
            aria-describedby={formErrors.pAdd ? "pAdd-error" : undefined}
          />
          {formErrors.pAdd && (
            <p id="pAdd-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.pAdd}
            </p>
          )}
        </div>

        {/* Education */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="Edu"
          >
            Education <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="Edu"
            name="Edu"
            placeholder="Enter Education"
            value={formData.Edu}
            onChange={handleInputChange}
            className={getInputClassName("Edu")}
            aria-invalid={!!formErrors.Edu}
            aria-describedby={formErrors.Edu ? "Edu-error" : undefined}
          />
          {formErrors.Edu && (
            <p id="Edu-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.Edu}
            </p>
          )}
        </div>

        {/* Dropdown: District */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="dId"
          >
            District <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="dId"
            name="dId"
            value={formData.dId}
            onChange={(val) => handleDropdownChange("dId", val)}
            options={districts}
            labelKey="Dist"
            valueKey="ID"
            className={getInputClassName("dId")}
            defaultOptionText={
              isLoadingDistricts
                ? "Loading districts..."
                : "-- Select District --"
            }
            disabled={isLoadingDistricts}
            aria-invalid={!!formErrors.dId}
            aria-describedby={formErrors.dId ? "dId-error" : undefined}
          />
          {formErrors.dId && (
            <p id="dId-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.dId}
            </p>
          )}
        </div>

        {/* Dropdown: Taluka */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="tId"
          >
            Taluka <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="tId"
            name="tId"
            value={formData.tId}
            onChange={(val) => handleDropdownChange("tId", val)}
            options={talukas}
            labelKey="Taluka"
            valueKey="ID"
            className={getInputClassName("tId")}
            defaultOptionText={
              isLoadingTalukas ? "Loading talukas..." : "-- Select Taluka --"
            }
            disabled={!formData.dId || isLoadingTalukas}
            aria-invalid={!!formErrors.tId}
            aria-describedby={formErrors.tId ? "tId-error" : undefined}
          />
          {formErrors.tId && (
            <p id="tId-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.tId}
            </p>
          )}
        </div>

        {/* Dropdown: Village */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="vId"
          >
            Village <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="vId"
            name="vId"
            value={formData.vId}
            onChange={(val) => handleDropdownChange("vId", val)}
            options={villages}
            labelKey="Gaam"
            valueKey="ID"
            className={getInputClassName("vId")}
            defaultOptionText={
              isLoadingVillages ? "Loading villages..." : "-- Select Village --"
            }
            disabled={!formData.tId || isLoadingVillages}
            aria-invalid={!!formErrors.vId}
            aria-describedby={formErrors.vId ? "vId-error" : undefined}
          />
          {formErrors.vId && (
            <p id="vId-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.vId}
            </p>
          )}
        </div>

        {/* Blood Group */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="bGroup"
          >
            Blood Group <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="bGroup"
            name="bGroup"
            placeholder="e.g., O+, A-, AB+"
            value={formData.bGroup}
            onChange={handleInputChange}
            className={getInputClassName("bGroup")}
            aria-invalid={!!formErrors.bGroup}
            aria-describedby={formErrors.bGroup ? "bGroup-error" : undefined}
          />
          {formErrors.bGroup && (
            <p id="bGroup-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.bGroup}
            </p>
          )}
        </div>

        {/* Mobile Number */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="Mob"
          >
            Mobile Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="Mob"
            name="Mob"
            placeholder="Enter Mobile Number"
            value={formData.Mob}
            onChange={handleInputChange}
            className={getInputClassName("Mob")}
            maxLength={15}
            aria-invalid={!!formErrors.Mob}
            aria-describedby={formErrors.Mob ? "Mob-error" : undefined}
          />
          {formErrors.Mob && (
            <p id="Mob-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.Mob}
            </p>
          )}
        </div>

        {/* Whatsapp Number */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="whtNo"
          >
            Whatsapp Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="whtNo"
            name="whtNo"
            placeholder="Enter Whatsapp Number"
            value={formData.whtNo}
            onChange={handleInputChange}
            className={getInputClassName("whtNo")}
            maxLength={15}
            aria-invalid={!!formErrors.whtNo}
            aria-describedby={formErrors.whtNo ? "whtNo-error" : undefined}
          />
          {formErrors.whtNo && (
            <p id="whtNo-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.whtNo}
            </p>
          )}
        </div>

        {/* Profession */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="Prof"
          >
            Profession <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="Prof"
            name="Prof"
            placeholder="Enter Profession"
            value={formData.Prof}
            onChange={handleInputChange}
            className={getInputClassName("Prof")}
            aria-invalid={!!formErrors.Prof}
            aria-describedby={formErrors.Prof ? "Prof-error" : undefined}
          />
          {formErrors.Prof && (
            <p id="Prof-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.Prof}
            </p>
          )}
        </div>

        {/* Designation */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="Designation"
          >
            Designation <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="Designation"
            name="Designation"
            placeholder="Enter Designation"
            value={formData.Designation}
            onChange={handleInputChange}
            className={getInputClassName("Designation")}
            aria-invalid={!!formErrors.Designation}
            aria-describedby={
              formErrors.Designation ? "Designation-error" : undefined
            }
          />
          {formErrors.Designation && (
            <p
              id="Designation-error"
              className="text-red-500 text-xs italic mt-1"
            >
              {formErrors.Designation}
            </p>
          )}
        </div>

        {/* Email */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="eMail"
          >
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="eMail"
            name="eMail"
            placeholder="Enter Email"
            value={formData.eMail}
            onChange={handleInputChange}
            className={getInputClassName("eMail")}
            aria-invalid={!!formErrors.eMail}
            aria-describedby={formErrors.eMail ? "eMail-error" : undefined}
          />
          {formErrors.eMail && (
            <p id="eMail-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.eMail}
            </p>
          )}
        </div>

        {/* Resident */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="Resident"
          >
            Resident Status <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="Resident"
            name="Resident"
            placeholder="Enter Resident Status"
            value={formData.Resident}
            onChange={handleInputChange}
            className={getInputClassName("Resident")}
            aria-invalid={!!formErrors.Resident}
            aria-describedby={
              formErrors.Resident ? "Resident-error" : undefined
            }
          />
          {formErrors.Resident && (
            <p id="Resident-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.Resident}
            </p>
          )}
        </div>

        {/* Dropdown: City */}
        <div className="form-group">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="cId"
          >
            City <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="cId"
            name="cId"
            value={formData.cId}
            onChange={(val) => handleDropdownChange("cId", val)}
            options={cities}
            labelKey="City"
            valueKey="ID"
            className={getInputClassName("cId")}
            defaultOptionText={
              isLoadingCities ? "Loading cities..." : "-- Select City --"
            }
            disabled={isLoadingCities}
            aria-invalid={!!formErrors.cId}
            aria-describedby={formErrors.cId ? "cId-error" : undefined}
          />
          {formErrors.cId && (
            <p id="cId-error" className="text-red-500 text-xs italic mt-1">
              {formErrors.cId}
            </p>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-center mt-5 gap-3">
        <button
          type="button"
          className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition duration-300 ease-in-out"
          onClick={handleSave}
        >
          Save User Details
        </button>
        <button
          onClick={onClose}
          className="px-5 py-2 bg-gray-300 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default UserDetailForm;
